'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent } from "./components/ui/Card"
import { Button } from "./components/ui/Button"
import { Input } from "./components/ui/Input"
import Header from "./components/ui/Header"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/Tabs"
import yaml from 'js-yaml'

function KVTable({ items, setItems, placeholderKey, placeholderValue }) {
    useEffect(() => {
        if (items.length === 0) setItems([{ key: '', value: '' }])
    }, [items, setItems])

    const update = (i, field, value) => {
        const next = [...items]
        next[i][field] = value
        setItems(next)
    }
    const add = () => setItems([...items, { key: '', value: '' }])
    const remove = (i) => setItems(items.filter((_, idx) => idx !== i))

    return (
        <div className="space-y-2">
            {items.map((kv, i) => (
                <div key={i} className="flex gap-2">
                    <Input
                        placeholder={placeholderKey}
                        value={kv.key}
                        onChange={e => update(i, 'key', e.target.value)}
                    />
                    <Input
                        className="w-full"
                        placeholder={placeholderValue}
                        value={kv.value}
                        onChange={e => update(i, 'value', e.target.value)}
                    />
                    <Button variant="outline" onClick={() => remove(i)}>×</Button>
                </div>
            ))}
            <Button variant="outline" onClick={add}>+ Add</Button>
        </div>
    )
}

export default function ApiClientUI() {
    const [docInfo, setDocInfo] = useState({description:'', url:''})
    const [title, setTitle] = useState('')
    const [product, setProduct] = useState(0)
    const [instance, setInstance] = useState(0)
    const [productUrl, setProductUrl] = useState('')
    const [method, setMethod] = useState('GET')
    const [url, setUrl] = useState('')
    const [bodyType, setBodyType] = useState('json')
    const [body, setBody] = useState('')
    const [formBody, setFormBody] = useState([{ key: '', value: '' }])
    const [response, setResponse] = useState('')
    const [responseJSON, setResponseJSON] = useState(null)
    const [iframeUrl, setIframeUrl] = useState(null)
    const [responseTab, setResponseTab] = useState('body')
    const [responseHeaders, setResponseHeaders] = useState({})

    const isContentTypeJson = ((responseHeaders['content-type'] || responseHeaders['Content-Type'] || '')).includes('application/json')

    const [headers, setHeaders] = useState([{ key: '', value: '' }])
    const [params, setParams] = useState([{ key: '', value: '' }])

    // OpenAPI / left-side interface selection
    const [spec, setSpec] = useState(null)
    const [pathList, setPathList] = useState([])
    const [selectedPath, setSelectedPath] = useState(null)
    const [selectedMethod, setSelectedMethod] = useState(null)
    const [specUrl, setSpecUrl] = useState('')

    const [authEnabled, setAuthEnabled] = useState(false)
    const [token, setToken] = useState('')

    const loadSpec = async (specUrlToLoad) => {
        try {
            const res = await fetch(specUrlToLoad, {
                credentials: 'include',
            })
            const contentType = res.headers.get('content-type') || ''
            let loaded
            if (contentType.includes('application/json')) {
                loaded = await res.json()
            } else {
                const text = await res.text()
                try { loaded = yaml.load(text) } catch { loaded = null }
            }
            if (!loaded) return
            console.log('OpenAPI spec', loaded)
            if(loaded.externalDocs){
                setDocInfo({description:loaded.externalDocs.description, url:loaded.externalDocs.url})
            }else{
                setDocInfo({description:'', url:''})
            }
            setTitle(loaded.info.title||'')
            setProduct(loaded.info.product||0)
            setInstance(loaded.info.instance||0)
            setProductUrl(loaded.info.url||'')
            setSpec(loaded)
            const paths = Object.keys(loaded.paths || {})
            setPathList(paths)

            // auto-select first path+method
            if (paths.length) {
                const p = paths[0]
                const methods = Object.keys(loaded.paths[p] || {})
                if (methods.length) applyOperation(p, methods[0], specUrlToLoad, loaded)
            }
        } catch (err) {
            console.error('Failed to load OpenAPI spec', err)
        }
    }

    const applyOperation = (p, m, specUrlLocal, specObj) => {
        const s = specObj || spec
        if (!s) return
        const pathObj = s.paths[p]
        if (!pathObj) return
        const operation = pathObj[m]
        if (!operation) return

        const base = (s.servers && s.servers[0] && s.servers[0].url) || (specUrlLocal ? (new URL(specUrlLocal).origin) : window.location.origin)
        let finalUrl = base.replace(/\/$/, '') + p
        finalUrl = finalUrl.replace(/\{[^}]+\}/g, '1')

        setSelectedPath(p)
        setSelectedMethod(m)
        setMethod(m.toUpperCase())
        setUrl(finalUrl)

        const paramsArr = operation.parameters || []
        const queryParams = paramsArr.filter(p => p.in === 'query').map(p => ({ key: p.name, value: p.example || '' }))
        setParams(queryParams)
        const headerParams = paramsArr.filter(p => p.in === 'header').map(p => ({ key: p.name, value: p.example || '' }))
        if(operation.responses && operation.responses['200'] && operation.responses['200'].content && operation.responses['200'].content['text/html']){
            headerParams.push({ key: 'Accept', value: 'text/html' })
        }
        setHeaders(headerParams)
        const postParams = paramsArr.filter(p => p.in === 'post').map(p => ({ key: p.name, value: p.example || '' }))
        setFormBody(postParams)
        

        const requestBody = operation.requestBody
        if (requestBody && requestBody.content) {
            const content = requestBody.content['application/json'] || requestBody.content[Object.keys(requestBody.content)[0]]
            if (content) {
                let example = content.example
                if (!example && content.examples) {
                    const key = Object.keys(content.examples)[0]
                    example = content.examples[key] && content.examples[key].value
                }
                if (!example && content.schema) {
                    example = generateExampleFromSchema(content.schema)
                }
                if (example) {
                    setBodyType('json')
                    setBody(JSON.stringify(example, null, 2))
                    setHeaders(prev => {
                        const next = [...prev]
                        const idx = next.findIndex(h => h.key.toLowerCase() === 'content-type')
                        if (idx === -1) next.push({ key: 'Content-Type', value: 'application/json' })
                        else next[idx].value = 'application/json'
                        return next
                    })
                }
            }
        }

        const responseContent = operation.responses && operation.responses['200'] && operation.responses['200'].content
        if (responseContent) {
            const resp = responseContent['application/json'] || responseContent[Object.keys(responseContent)[0]]
            if (resp && resp.examples) {
                const key = Object.keys(resp.examples)[0]
                const example = resp.examples[key] && resp.examples[key].value
                if (example) {
                    setResponseJSON(example)
                    setResponseTab('json')
                }
            }
        }
    }

    useEffect(() => {
        const tryApplyFromHash = () => {
            const hash = window.location.hash.replace(/^#/, '')
            const params = new URLSearchParams(hash)
            let openapiUrl = params.get('openapi')
            const marketId = params.get('market')
            if(marketId){
                openapiUrl = 'https://api.cn/market/'+marketId+'.json'
            }
            if (openapiUrl){
                console.log('Applying openapi from hash', openapiUrl);
                setSpecUrl(openapiUrl)
                loadSpec(openapiUrl)
            }
        }
        window.addEventListener('hashchange', tryApplyFromHash)
        tryApplyFromHash()
        return () => window.removeEventListener('hashchange', tryApplyFromHash)
    }, []);

    const buildUrl = () => {
        const u = new URL(url)
        params.forEach(p => {
            if (p.key) u.searchParams.set(p.key, p.value)
        })
        return u.toString()
    }

    const buildHeaders = () => {
        const h = {}
        headers.forEach(kv => {
            if (kv.key) h[kv.key] = kv.value
        })
        if (authEnabled && token) {
            h['Authorization'] = `Bearer ${token}`
        }
        if (!h['Content-Type']) {
            if (bodyType === 'json' && body) h['Content-Type'] = 'application/json'
            if (bodyType === 'form') h['Content-Type'] = 'application/x-www-form-urlencoded'
            if (bodyType === 'text') h['Content-Type'] = 'text/plain'
        }
        return h
    }

    const send = async () => {
        try {
            setResponseTab('body')
            setIframeUrl(null)
            setResponse('Loading...')
            setResponseJSON(null)
            setResponseHeaders({})

            let requestBody
            if (!['GET', 'HEAD'].includes(method)) {
                if (bodyType === 'json' || bodyType === 'text') requestBody = body
                if (bodyType === 'form') {
                    const params = new URLSearchParams()
                    formBody.forEach(kv => {
                        if (kv.key) params.append(kv.key, kv.value)
                    })
                    requestBody = params.toString()
                }
            }

            if(headers.find(h => h.key === 'Accept' && h.value === 'text/html')){
                setResponse('Load page')
                setIframeUrl(buildUrl())
                setResponseTab('iframe')
                return
            }
            if(url.includes('.html') && method === 'GET'){
                setResponse('Load page')
                setIframeUrl(buildUrl())
                setResponseTab('iframe')
                return
            }

            const res = await fetch(buildUrl(), {
                method,
                headers: buildHeaders(),
                body: requestBody
            })

            const text = await res.text()
            const hdrs = {}
            res.headers.forEach((v, k) => (hdrs[k] = v))
            setResponseHeaders(hdrs)
            setResponse(`Status: ${res.status}\n\n${text}`)
            
            try {
                setResponseJSON(JSON.parse(text))
                setResponseTab('json')
                if(!isContentTypeJson){
                    hdrs['content-type'] = 'application/json; add-from=text parse'
                    setResponseHeaders(hdrs)
                }
            } catch {
                setResponseJSON(null)
            }
        } catch (e) {
            setResponse(String(e))
            setResponseJSON(null)
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 p-3">
            <Header />
            <Card className="max-w-screen-2xl mx-auto rounded-2xl shadow">
                <CardContent className="p-6 space-y-4">
                    <div className="flex gap-4">
                        {/* Left sidebar */}
                        <div className="w-64 border-r pr-4">
                            <div className="mb-4">
                                <div className="text-sm font-semibold mb-2">OpenAPI Spec</div>
                                <div className="flex gap-2">
                                    <Input value={specUrl}  className="px-1 py-1 text-xs rounded-xs" onChange={e => setSpecUrl(e.target.value)} placeholder="OpenAPI URL" />
                                    <Button className="px-1 py-1 text-xs rounded-xs" onClick={() => loadSpec(specUrl)}>Load</Button>
                                </div>
                            </div>

                            {docInfo.url && (
                                <div className="flex gap-2 mb-3">
                                <a href={docInfo.url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline">
                                    {docInfo.description || docInfo.url}
                                </a>
                                </div>
                            )}

                            <div>
                                <div className="text-sm font-semibold mb-2">APIs</div>
                                <div className="space-y-2 max-h-full overflow-auto">
                                    {pathList.length ? pathList.map(p => (
                                        <div key={p} className={`p-2 rounded hover:bg-gray-100 cursor-pointer ${selectedPath === p ? 'bg-gray-200' : ''}`} tabIndex={0}
                                             onClick={() => { const methods = Object.keys(spec.paths[p] || {}); if (methods.length) applyOperation(p, methods[0]); }}
                                             onKeyDown={(e) => { if (e.key === 'Enter') { const methods = Object.keys(spec.paths[p] || {}); if (methods.length) applyOperation(p, methods[0]); } }}
                                        >
                                                <div className="flex items-center gap-1 flex-1 min-w-0">
                                                    {Object.keys((spec && spec.paths && spec.paths[p]) || {}).map(m => (
                                                        <Button key={m} className="px-0.5 py-0 text-xs rounded-xs" onClick={(e) => { e.stopPropagation(); applyOperation(p, m) }}>
                                                            {m.toUpperCase()}
                                                        </Button>
                                                    ))}
                                                    <div className="ml-2 text-xs text-gray-500 truncate">{p}</div>
                                                </div>
                                            {spec && spec.paths && spec.paths[p] && spec.paths[p][Object.keys(spec.paths[p])[0]] && (
                                                <div className="text-sm text-gray-600 ml-0 truncate max-w-[180px]">{spec.paths[p][Object.keys(spec.paths[p])[0]].summary}</div>
                                            )}
                                        </div>
                                    )) : (
                                        <div className="text-sm text-gray-500">No spec loaded</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Main panel */}
                        <div className="flex-1 w-4/6">
                            {title && (
                                <div className="flex gap-2 mb-3">
                                    {instance > 0 ? (
                                        <a href={productUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline">
                                            {title || "产品"} 介绍
                                        </a>
                                    ) : (
                                        <a href={productUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline">
                                            {title || "产品"} 购买
                                        </a>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-2 mb-3">
                                <span className="text-gray-500">本页面用于发起接口调试，调用接口会消耗真实费用，测试不能替代实际环境。</span>
                            </div>

                            <div className="flex gap-2 mb-3">
                                <select
                                    value={method}
                                    onChange={e => setMethod(e.target.value)}
                                    className="border rounded-xl px-3 py-2"
                                >
                                    {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => (
                                        <option key={m}>{m}</option>
                                    ))}
                                </select>
                                <Input
                                    value={url}
                                    onChange={e => setUrl(e.target.value)}
                                    className="flex-1"
                                    placeholder="Request URL"
                                />
                                <Button className="rounded-xl" onClick={send}>Send</Button>
                            </div>

                            <Tabs defaultValue="headers" className="w-full mt-3">
                                <TabsList>
                                    <TabsTrigger value="headers">Headers</TabsTrigger>
                                    <TabsTrigger value="params">Params</TabsTrigger>
                                    <TabsTrigger value="auth">Auth</TabsTrigger>
                                    <TabsTrigger value="body">Body</TabsTrigger>
                                </TabsList>

                                <TabsContent value="headers">
                                    <KVTable items={headers} setItems={setHeaders} placeholderKey="Header" placeholderValue="Value" />
                                </TabsContent>

                                <TabsContent value="params">
                                    <KVTable items={params} setItems={setParams} placeholderKey="Param" placeholderValue="Value" />
                                </TabsContent>

                                <TabsContent value="auth" className="space-y-3">
                                    <label className="flex items-center gap-2 text-sm mb-2">
                                        <input type="checkbox" checked={authEnabled} onChange={e => setAuthEnabled(e.target.checked)} />
                                        Bearer Token
                                    </label>
                                    {authEnabled && (
                                        <Input placeholder="Bearer token" value={token} onChange={e => setToken(e.target.value)} />
                                    )}
                                </TabsContent>

                                <TabsContent value="body" className="space-y-3">
                                    <div className="flex gap-2 mb-3">
                                        <select value={bodyType} onChange={e => setBodyType(e.target.value)} className="border rounded-xl px-3 py-2">
                                            <option value="json">JSON</option>
                                            <option value="form">x-www-form-urlencoded</option>
                                            <option value="text">Raw Text</option>
                                        </select>
                                    </div>
                                    {bodyType === 'json' && <textarea className="w-full h-40 border rounded-xl p-3 font-mono" placeholder="JSON body" value={body} onChange={e => setBody(e.target.value)} />}
                                    {bodyType === 'text' && <textarea className="w-full h-40 border rounded-xl p-3 font-mono" placeholder="Raw text body" value={body} onChange={e => setBody(e.target.value)} />}
                                    {bodyType === 'form' && <KVTable items={formBody} setItems={setFormBody} placeholderKey="Field" placeholderValue="Value" />}
                                </TabsContent>
                            </Tabs>

                            <hr className="my-4" />

                            <div className="space-y-2">
                                <div className="text-sm font-semibold">Response Headers</div>
                                <pre className="bg-gray-100 p-3 rounded-xl text-xs whitespace-pre-wrap break-words overflow-auto">
                                    {Object.keys(responseHeaders).length ? JSON.stringify(responseHeaders, null, 2) : 'No headers'}
                                </pre>

                                <Tabs value={responseTab} onValueChange={setResponseTab} className="w-full">
                                    <TabsList>
                                        <TabsTrigger value="body">Body</TabsTrigger>
                                        {isContentTypeJson && (
                                            <TabsTrigger value="json" disabled={!responseJSON}>JSON</TabsTrigger>
                                        )}
                                        {iframeUrl && (
                                            <TabsTrigger value="iframe" disabled={!iframeUrl}>Page</TabsTrigger>
                                        )}
                                    </TabsList>

                                    <TabsContent value="body">
                                        <pre className="bg-black text-green-400 p-4 rounded-xl whitespace-pre-wrap break-words overflow-auto text-sm w-auto">
                                            {response || 'Response will appear here'}
                                        </pre>
                                    </TabsContent>

                                    {isContentTypeJson && (
                                        <TabsContent value="json">
                                            {responseJSON ? (
                                                <pre className="bg-gray-800 text-green-300 p-4 rounded-xl whitespace-pre-wrap break-words overflow-auto text-sm w-auto">
                                                    {JSON.stringify(responseJSON, null, 2)}
                                                </pre>
                                            ) : (
                                                <div className="p-4 text-sm text-gray-500">Response is not valid JSON.</div>
                                            )}
                                        </TabsContent>
                                    )}
                                    {iframeUrl && (
                                        <TabsContent value="iframe">
                                            {iframeUrl ? (
                                                //<pre className="bg-gray-800 text-green-300 p-4 rounded-xl whitespace-pre-wrap break-words overflow-auto text-sm w-auto">
                                                //    {responseHTML}
                                                //</pre>
                                                <iframe src={`${iframeUrl}`} className="w-full h-96" />
                                            ) : (
                                                <div className="p-4 text-sm text-gray-500">Response is not valid Page.</div>
                                            )}
                                        </TabsContent>
                                    )}
                                </Tabs>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
